<template>
  <div v-if="profileLoaded && loaded" class="edit-form">
    <form class="cb-form" @submit.prevent="submitted">
      <div class="form-group">
        <text-field
          id="title"
          label="Title"
          placeholder="The title for the block."
          v-model="v$.title.$model"
          :disabled="!canUpdateContentBlocks"
          mandatory
        >
          <template v-slot:validation v-if="v$.title.$error">
            <validation-error v-if="!v$.title.required"
              >You must specify a title.</validation-error
            >
          </template>
        </text-field>
        <text-field
          multiline
          id="description"
          label="Description"
          placeholder="A brief description of the block."
          v-model="v$.description.$model"
          :disabled="!canUpdateContentBlocks"
          mandatory
        >
          <template v-slot:validation v-if="v$.description.$error">
            <validation-error v-if="!v$.description.required"
              >You must provide a description.</validation-error
            >
          </template>
        </text-field>
        <switch-field
          v-if="canUpdateContentBlocks"
          v-model="active"
          id="template-active"
          label="Active"
        ></switch-field>
        <switch-field
          id="avail-template-authors"
          v-model="availableForTemplateAuthors"
          v-if="isDocumentAdmin"
          label="Enable for Template Authors"
        ></switch-field>
        <switch-field
          id="avail-document-authors"
          v-model="availableForDocumentAuthors"
          label="Enable for Document Authors"
          v-if="isDocumentAdmin"
        ></switch-field>
        <tag-field
          class="cb-tags"
          label="Tags"
          list-position="above"
          v-model="tags"
          :existing-tags="existingTags"
          :disabled="!canUpdateContentBlocks"
        ></tag-field>
        <div v-if="version">
          <label class="form-label">Latest Version</label>
          <div>
            {{ toUiVersion(version.versionNumber) }}:
            {{ $filters.long(version.createdDate) }}
          </div>
        </div>
        <div v-if="publishedVersion">
          <label class="form-label">Published Version</label>
          <div>
            {{ toUiVersion(publishedVersion.versionNumber) }}:
            {{ publishedVersion.createdDate | long }}
          </div>
        </div>
        <form-buttons>
          <button
            v-if="canUpdateContentBlocks || isDocumentAdmin"
            class="btn btn-primary"
            :disabled="v$.$invalid"
          >
            Save
          </button>
          <button type="button" class="btn btn-secondary" @click="closeClicked">
            Close
          </button>
        </form-buttons>
      </div>
    </form>
  </div>
</template>
<script>
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import TagField from "@/components/common/tags/TagField.vue";
import docGenTags from "@/services/sharedo/docGenTagsService";
import docGenTemplates from "@/services/sharedo/docGenTemplatesService";
import { toSaveRequest } from "@/mappers/sharedo/content-blocks/contentBlockMapper";
import { ascending, descending } from "@/util/sort";
import profile from "@/mixins/profile";
import { toUiVersion } from "@/util/versionUtil";
import {
  ADMIN_DOCUMENT_ASSEMBLY,
  CONTENT_BLOCKS_READ,
  CONTENT_BLOCKS_CREATE,
} from "@/constants/permissions";
import {
  CONTENT_BLOCK_DOCUMENT,
  CONTENT_BLOCK_HTML,
} from "@/constants/templateTypes";

const loadExistingTags = async function () {
  this.logDebug("Loading existing tags.");

  try {
    const response = await docGenTags.getAll();
    if (response.tags.length > 0) {
      const tags = response.tags.map((t) => ({ id: t, value: t }));
      tags.sort(ascending((x) => x.value));

      this.existingTags = tags;
    }

    this.logInfo("Loaded existing tags.", response);
  } catch (error) {
    this.logError("Unable to load existing tags.", { error });
    throw error;
  }
};

const loadContentBlock = async function () {
  this.logDebug("Loading content block", { systemName: this.systemName });

  try {
    this.contentBlock = await docGenTemplates.getOne(this.systemName);
    this.title = this.contentBlock.title;
    this.description = this.contentBlock.description;
    this.tags = this.contentBlock.categories;
    this.active = this.contentBlock.active;
    this.availableForDocumentAuthors =
      this.contentBlock.contentBlock.availableForDocumentAuthors;
    this.availableForTemplateAuthors =
      this.contentBlock.contentBlock.availableForTemplateAuthors;

    if (this.contentBlock.templateType === CONTENT_BLOCK_HTML) {
      const versions = await docGenTemplates.getSourceVersions(
        this.contentBlock.documentPackContents[0].sources[0].id
      );
      versions.sort(descending((v) => v.versionNumber));

      this.version = versions[0];
      this.publishedVersion = versions.find(
        (version) => version.status === "published"
      );
    }

    this.loaded = true;
    this.logInfo("Loaded content block", this.contentBlock);
  } catch (error) {
    this.logError("Unable to load content block.", {
      systemName: this.systemName,
      error,
    });
    this.$store.commit("error", { message: "Unable to load content block." });
    this.contentBlock = null;
  }
};

const closeClicked = function () {
  this.logDebug("User has closed the form.");
  this.$emit("close");
};

const submitted = async function () {
  this.v$.$touch();

  if (this.v$.$invalid) {
    this.submitStatus = "ERROR";
  } else {
    this.submitStatus = "PENDING";

    this.contentBlock.title = this.title;
    this.contentBlock.description = this.description;
    this.contentBlock.active = this.active;
    this.contentBlock.categories = this.tags;
    this.contentBlock.contentBlock.availableForTemplateAuthors =
      this.availableForTemplateAuthors;
    this.contentBlock.contentBlock.availableForDocumentAuthors =
      this.availableForDocumentAuthors;

    this.logDebug("Saving content block.", this.contentBlock);

    try {
      await docGenTemplates.save(
        this.systemName,
        toSaveRequest(this.contentBlock)
      );

      this.logInfo("Content block saved.", this.contentBlock);
      this.$emit("close", { reload: true });
    } catch (error) {
      this.logError("Unable to save content block.", {
        contentBlock: this.contentBlock,
        error,
      });
      this.$store.commit("error", { message: "Unable to save content block." });
    }
  }
};

export default {
  setup() {
    return { v$: useVuelidate() }
  },
  components: { TagField },
  mixins: [profile],
  props: {
    systemName: {
      type: String,
      required: true,
    },
  },
  data: function () {
    return {
      contentBlock: null,
      version: null,
      title: "",
      description: "",
      active: true,
      availableForDocumentAuthors: true,
      availableForTemplateAuthors: true,
      tags: [],
      existingTags: [],
      loaded: false,
      publishedVersion: null,
    };
  },
  computed: {
    isDocumentAdmin: function () {
      return this.hasPermission(ADMIN_DOCUMENT_ASSEMBLY);
    },
    canReadContentBlocks: function () {
      return this.hasPermission(CONTENT_BLOCKS_READ);
    },
    canUpdateContentBlocks: function () {
      return this.hasPermission(CONTENT_BLOCKS_CREATE);
    },
  },
  validations() {
    return {
      title: {
        required,
      },
      description: {
        required,
      },
    };
  },
  methods: {
    loadContentBlock,
    loadExistingTags,
    closeClicked,
    submitted,
    toUiVersion,
  },
  watch: {
    systemName: async function (newValue) {
      if (newValue) {
        await loadContentBlock();
      }
    },
  },
  emits: ["close"],
  created: async function () {
    await this.loadExistingTags();
    await this.loadContentBlock();
  },
};
</script>
